import { FilterChipDef } from "./filterChipDefs";

type GetLabel = (field: string) => string;

export function getMatchTerms(chipDef: FilterChipDef, getLabel: GetLabel): string[] {
  const dynamic = chipDef.ownedKeys.map((k) => getLabel(k as string));
  return [chipDef.label, ...(chipDef.synonyms ?? []), ...dynamic].map((t) =>
    t.toLowerCase(),
  );
}

type RankTier = 0 | 1 | 2; // 0 = exact, 1 = prefix, 2 = substring

function bestTier(terms: string[], q: string): RankTier | null {
  let best: RankTier | null = null;
  for (const term of terms) {
    if (term === q) return 0; // exact wins immediately
    if (term.startsWith(q)) {
      if (best === null || best > 1) best = 1;
    } else if (term.includes(q)) {
      if (best === null) best = 2;
    }
  }
  return best;
}

export function rankMatches(
  rawQuery: string,
  defs: FilterChipDef[],
  getLabel: GetLabel,
): FilterChipDef[] {
  const q = rawQuery.trim().toLowerCase();
  if (q === "") {
    return [...defs].sort((a, b) => a.priority - b.priority);
  }
  const scored: { def: FilterChipDef; tier: RankTier }[] = [];
  for (const def of defs) {
    const terms = getMatchTerms(def, getLabel);
    const tier = bestTier(terms, q);
    if (tier !== null) scored.push({ def, tier });
  }
  scored.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    return a.def.priority - b.def.priority;
  });
  return scored.map((s) => s.def);
}

export function extractResidual(rawQuery: string, matchedTerm: string): string {
  const idx = rawQuery.toLowerCase().indexOf(matchedTerm.toLowerCase());
  if (idx === -1) return rawQuery.trim();
  const before = rawQuery.slice(0, idx);
  const after = rawQuery.slice(idx + matchedTerm.length);
  return `${before}${after}`.trim();
}

/**
 * Given a query and a chipDef, return the longest match term (synonym, static label,
 * or dynamic field label) that appears as a substring of the query. Used by the
 * orchestrator to compute residual after a suggestion is picked.
 */
export function findBestMatchedTerm(
  rawQuery: string,
  chipDef: FilterChipDef,
  getLabel: GetLabel,
): string | null {
  const q = rawQuery.toLowerCase();
  const terms = getMatchTerms(chipDef, getLabel);
  const hits = terms.filter((t) => t.length > 0 && q.includes(t));
  if (hits.length === 0) return null;
  return hits.reduce((longest, t) => (t.length > longest.length ? t : longest), hits[0]);
}
